import React, { useEffect, useState } from 'react';
import { Button, Card, Row, Col, InputNumber, Select, Space } from 'antd';

const { Option } = Select;

interface RedemptionPoolChartProps {
  drip?: number;
  newPurchase?: number;
 // carryOver?: number;
  totalRedemption?: number;
  totalMatch?: number;
  totalNonMatch?: number;
  totalRetracted?: number;
  matchPeriodAllocated?: number;
  // Optional controlled period. When provided together with onPeriodChange the
  // dropdowns are controlled by the parent so a sibling table can react to it.
  month?: number;
  year?: number;
  onPeriodChange?: (month: number, year: number) => void;
  editable?: boolean;
  onPoolSave?: (dripTotal: number, newPurchaseTotal: number) => Promise<void> | void;
  // Per-EMD-firm redemption totals. When provided, the progress ring is split
  // into a differently coloured segment per firm (sized by each firm's total).
  redemptionByFirm?: Array<{ firm: string; amount: number }>;
}

//const DEFAULT_CARRY_OVER = 1158655;
const DEFAULT_TOTAL_REDEMPTION = 0;

const COLORS = {
  drip: '#52c41a', // green
  newPurchase: '#fa8c16', // orange
 // carryOver: '#1890ff', // blue
  progress: '#1890ff', // blue accent for progress ring
  track: '#f0f0f0', // grey track
};

// Distinct colours cycled per EMD firm in the progress ring.
const FIRM_COLORS = [
  '#1890ff', // blue
  '#722ed1', // purple
  '#13c2c2', // cyan
  '#eb2f96', // magenta
  '#faad14', // gold
  '#a0d911', // lime
  '#f5222d', // red
  '#2f54eb', // geek blue
];

const formatCurrency = (value: number): string =>
  `$${value.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

export const getActiveRedemptionTotal = (
  requests: Array<{
    amount?: number | string | null;
    status?: string | null;
  }>,
): number =>
  requests
    .filter((request) => {
      const status = (request.status || '').trim().toLowerCase();
      return status !== 'cancelled' && status !== 'cancel';
    })
    .reduce((total, request) => {
      const amount =
        typeof request.amount === 'string'
          ? Number(request.amount.replace(/[$,]/g, ''))
          : Number(request.amount);
      return total + (Number.isFinite(amount) ? amount : 0);
    }, 0);

// Month/year selector options
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const YEARS = [2026, 2027, 2028, 2029, 2030, 2031, 2032, 2033, 2034, 2035];
const DEFAULT_MONTH = 5; // June (0-indexed)
const DEFAULT_YEAR = 2026;

export const getCurrentRedemptionPeriod = (now: Date = new Date()) => {
  const monthsBack = now.getDate() <= 18 ? 2 : 1;
  const period = new Date(now.getFullYear(), now.getMonth() - monthsBack, 1);
  return { month: period.getMonth(), year: period.getFullYear() };
};

// A period is locked once it is two or more months behind the current month;
// its pool figures and retraction list can no longer be changed.
export const isRedemptionPeriodLocked = (
  month: number,
  year: number,
  now: Date = new Date(),
): boolean =>
  (now.getFullYear() * 12 + now.getMonth()) - (year * 12 + month) >= 2;

// SVG donut geometry: a circle of radius 15.9155 has a circumference of ~100,
// so stroke-dasharray values map directly to percentages.
const RADIUS = 15.9155;
const STROKE_WIDTH = 4;
// Rendered size of each circle chart (35% larger than the original 160px).
const CHART_SIZE = 216;

interface Segment {
  label: string;
  value: number;
  color: string;
}

const BreakdownDonut: React.FC<{ segments: Segment[]; total: number }> = ({
  segments,
  total,
}) => {
  let cumulative = 0;

  return (
    <svg viewBox="0 0 42 42" width={CHART_SIZE} height={CHART_SIZE} role="img" aria-label="Redemption pool breakdown">
      <circle
        cx="21"
        cy="21"
        r={RADIUS}
        fill="none"
        stroke={COLORS.track}
        strokeWidth={STROKE_WIDTH}
      />
      {segments.map((segment) => {
        const percent = total > 0 ? (segment.value / total) * 100 : 0;
        // dashoffset positions each segment; 25 aligns the start to 12 o'clock.
        const dashOffset = 25 - cumulative;
        cumulative += percent;
        return (
          <circle
            key={segment.label}
            cx="21"
            cy="21"
            r={RADIUS}
            fill="none"
            stroke={segment.color}
            strokeWidth={STROKE_WIDTH}
            strokeDasharray={`${percent} ${100 - percent}`}
            strokeDashoffset={dashOffset}
          />
        );
      })}
      <text
        x="21"
        y="22.5"
        textAnchor="middle"
        style={{ fontSize: '3.2px', fontWeight: 600, fill: '#262626' }}
      >
        {formatCurrency(total)}
      </text>
    </svg>
  );
};

interface FirmSegment {
  firm: string;
  amount: number;
  color: string;
  percent: number; // percent of the total pool
}

const ProgressRing: React.FC<{ percent: number; firmSegments?: FirmSegment[] }> = ({
  percent,
  firmSegments,
}) => {
  const clamped = Math.max(0, Math.min(100, percent));
  let cumulative = 0;
  return (
    <svg viewBox="0 0 42 42" width={CHART_SIZE} height={CHART_SIZE} role="img" aria-label="Redemption progress">
      <circle
        cx="21"
        cy="21"
        r={RADIUS}
        fill="none"
        stroke={COLORS.track}
        strokeWidth={STROKE_WIDTH}
      />
      {firmSegments && firmSegments.length > 0 ? (
        firmSegments.map((seg) => {
          const dashOffset = 25 - cumulative;
          cumulative += seg.percent;
          return (
            <circle
              key={seg.firm}
              cx="21"
              cy="21"
              r={RADIUS}
              fill="none"
              stroke={seg.color}
              strokeWidth={STROKE_WIDTH}
              strokeDasharray={`${seg.percent} ${100 - seg.percent}`}
              strokeDashoffset={dashOffset}
            />
          );
        })
      ) : (
        <circle
          cx="21"
          cy="21"
          r={RADIUS}
          fill="none"
          stroke={COLORS.progress}
          strokeWidth={STROKE_WIDTH}
          strokeDasharray={`${clamped} ${100 - clamped}`}
          strokeDashoffset={25}
          strokeLinecap="round"
        />
      )}
      <text
        x="21"
        y="22.5"
        textAnchor="middle"
        style={{ fontSize: '6px', fontWeight: 700, fill: COLORS.progress }}
      >
        {clamped.toFixed(1)}%
      </text>
    </svg>
  );
};

const LegendItem: React.FC<{
  color: string;
  label: string;
  value: number;
  percent: number;
}> = ({ color, label, value, percent }) => (
    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
    <span
      style={{
        display: 'inline-block',
        width: 12,
        height: 12,
        borderRadius: 2,
        backgroundColor: color,
        marginRight: 8,
        flexShrink: 0,
      }}
    />
      <div style={{ flex: 1 }}>
        <div>
          <div style={{ fontSize: 13, color: '#8c8c8c' }}>{label}</div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
          <span style={{ fontSize: 15, fontWeight: 600, color: '#262626' }}>
            {formatCurrency(value)}
          </span>
          <span style={{ fontSize: 15, color: '#8c8c8c', fontWeight: 400, whiteSpace: 'nowrap' }}>
            ({percent.toFixed(1)}%)
          </span>
          </div>
        </div>
      </div>
  </div>
);

const RedemptionPoolChart: React.FC<RedemptionPoolChartProps> = ({
  drip = 0,
  newPurchase = 0,
 // carryOver = DEFAULT_CARRY_OVER,
  totalRedemption = DEFAULT_TOTAL_REDEMPTION,
  totalMatch = 0,
  totalNonMatch = 0,
  totalRetracted = 0,
  matchPeriodAllocated = 0,
  month,
  year,
  onPeriodChange,
  editable = false,
  onPoolSave,
  redemptionByFirm,
}) => {
  const [internalMonth, setInternalMonth] = useState<number>(DEFAULT_MONTH);
  const [internalYear, setInternalYear] = useState<number>(DEFAULT_YEAR);
  const [draftDrip, setDraftDrip] = useState<number>(drip);
  const [draftNewPurchase, setDraftNewPurchase] = useState<number>(newPurchase);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [savingPool, setSavingPool] = useState<boolean>(false);

  useEffect(() => {
    setDraftDrip(drip);
    setDraftNewPurchase(newPurchase);
  }, [drip, newPurchase]);

  const selectedMonth = month ?? internalMonth;
  const selectedYear = year ?? internalYear;
  const isPreviousMonth = isRedemptionPeriodLocked(selectedMonth, selectedYear);

  useEffect(() => {
    if (isPreviousMonth && isEditing) {
      setDraftDrip(drip);
      setDraftNewPurchase(newPurchase);
      setIsEditing(false);
    }
  }, [isPreviousMonth, isEditing, drip, newPurchase]);

  const handleMonthChange = (value: number) => {
    setInternalMonth(value);
    if (onPeriodChange) onPeriodChange(value, selectedYear);
  };

  const handleYearChange = (value: number) => {
    setInternalYear(value);
    if (onPeriodChange) onPeriodChange(selectedMonth, value);
  };

  const displayedDrip = editable && isEditing ? draftDrip : drip;
  const displayedNewPurchase = editable && isEditing ? draftNewPurchase : newPurchase;
  const totalPool = displayedDrip + displayedNewPurchase;
  const poolLeft = Math.max(0, totalPool - matchPeriodAllocated);

  const savePool = async () => {
    if (!onPoolSave) return;
    setSavingPool(true);
    try {
      await onPoolSave(displayedDrip, displayedNewPurchase);
      setIsEditing(false);
    } finally {
      setSavingPool(false);
    }
  };

  const cancelPoolEdit = () => {
    setDraftDrip(drip);
    setDraftNewPurchase(newPurchase);
    setIsEditing(false);
  };

  // Build matched per-firm segments against all requests in the selected month.
  const firmAggregated = (redemptionByFirm || []).filter((f) => f.amount > 0);
  const effectiveRedemption = Math.max(0, totalRedemption);
  const firmSegments: FirmSegment[] = firmAggregated.map((f, i) => ({
    firm: f.firm || 'Unknown',
    amount: f.amount,
    color: FIRM_COLORS[i % FIRM_COLORS.length],
    percent: effectiveRedemption > 0 ? (f.amount / effectiveRedemption) * 100 : 0,
  }));

  const progressPercent =
    effectiveRedemption > 0
      ? ((totalMatch + totalRetracted) / effectiveRedemption) * 100
      : 0;

  const segments: Segment[] = [
    { label: 'Dividend Reinvestment Plan', value: displayedDrip, color: COLORS.drip },
    { label: 'New Purchase Total', value: displayedNewPurchase, color: COLORS.newPurchase },
 //   { label: 'Carry Over Amount', value: carryOver, color: COLORS.carryOver },
  ];

  const title = (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 14,
        marginTop: 14,
        width: '100%',
        textAlign: 'center',
      }}
    >
      <span style={{ fontSize: 30, fontWeight: 600 }}>Early Access Program</span>
      <Space size="small" style={{ justifyContent: 'center' }}>
        <Select
          value={selectedMonth}
          onChange={handleMonthChange}
          style={{ width: 130 }}
          aria-label="Select month"
        >
          {MONTHS.map((month, index) => (
            <Option key={month} value={index}>
              {month}
            </Option>
          ))}
        </Select>
        <Select
          value={selectedYear}
          onChange={handleYearChange}
          style={{ width: 90 }}
          aria-label="Select year"
        >
          {YEARS.map((year) => (
            <Option key={year} value={year}>
              {year}
            </Option>
          ))}
        </Select>
      </Space>
    </div>
  );

  return (
    <Card title={title} style={{ marginBottom: '18px' }}>
      <Row gutter={24} align="middle" justify="center">
        <Col xs={24} md={6} style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: '#262626', marginBottom: 8 }}>
            Investment Subscription
          </div>
          <BreakdownDonut segments={segments} total={totalPool} />
          <div style={{ marginTop: 8, color: '#8c8c8c' }}>
{/*             <div style={{ fontSize: 13 }}>Total Pool</div>
 */}            <div style={{ fontSize: 15, fontWeight: 600, color: '#262626' }}>
              Left:{formatCurrency(poolLeft)}
            </div>
          </div>
        </Col>

        <Col xs={24} md={6}>
          {editable && isEditing ? (
            <>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
                <span style={{ width: 12, height: 12, borderRadius: 2, backgroundColor: COLORS.drip, marginRight: 8, flexShrink: 0 }} />
                <InputNumber
                  min={0}
                  precision={2}
                  value={draftDrip}
                  formatter={(value) => `$${value}`}
                  parser={(value) => Number((value || '').replace(/[$,]/g, '')) || 0}
                  onChange={(value) => setDraftDrip(Number(value) || 0)}
                  style={{ width: '15ch' }}
                />
                <span style={{ marginLeft: 8, fontSize: 15, color: '#8c8c8c', whiteSpace: 'nowrap' }}>
                  ({totalPool > 0 ? ((draftDrip / totalPool) * 100).toFixed(1) : '0.0'}%)
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
                <span style={{ width: 12, height: 12, borderRadius: 2, backgroundColor: COLORS.newPurchase, marginRight: 8, flexShrink: 0 }} />
                <InputNumber
                  min={0}
                  precision={2}
                  value={draftNewPurchase}
                  formatter={(value) => `$${value}`}
                  parser={(value) => Number((value || '').replace(/[$,]/g, '')) || 0}
                  onChange={(value) => setDraftNewPurchase(Number(value) || 0)}
                  style={{ width: '15ch' }}
                />
                <span style={{ marginLeft: 8, fontSize: 15, color: '#8c8c8c', whiteSpace: 'nowrap' }}>
                  ({totalPool > 0 ? ((draftNewPurchase / totalPool) * 100).toFixed(1) : '0.0'}%)
                </span>
              </div>
              <Space>
                <Button type="primary" loading={savingPool} onClick={savePool}>Save</Button>
                <Button disabled={savingPool} onClick={cancelPoolEdit}>Cancel</Button>
              </Space>
            </>
          ) : editable ? (
            <>
              <LegendItem
                color={COLORS.drip}
                label="Dividend Reinvestment Plan"
                value={drip}
                percent={totalPool > 0 ? (drip / totalPool) * 100 : 0}
              />
              <LegendItem
                color={COLORS.newPurchase}
                label="New Purchase Total"
                value={newPurchase}
                percent={totalPool > 0 ? (newPurchase / totalPool) * 100 : 0}
              />
              <Button
                type="primary"
                disabled={isPreviousMonth}
                onClick={() => setIsEditing(true)}
              >
                Edit
              </Button>
            </>
          ) : (
            segments.map((segment) => (
              <LegendItem
                key={segment.label}
                color={segment.color}
                label={segment.label}
                value={segment.value}
                percent={totalPool > 0 ? (segment.value / totalPool) * 100 : 0}
              />
            ))
          )}
        </Col>

        <Col xs={24} md={6} style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: '#262626', marginBottom: 8 }}>
            Redemption Request
          </div>
          <ProgressRing
            percent={progressPercent}
            firmSegments={firmSegments.length > 0 ? firmSegments : undefined}
          />
          <div style={{ marginTop: 8 }}>
{/*             <div style={{ fontSize: 13, color: '#8c8c8c' }}>Total Redemption</div>
 */}            <div style={{ fontSize: 15, fontWeight: 600, color: '#262626' }}>
{/*               {formatCurrency(effectiveRedemption)} / {formatCurrency(totalPool)}
               {formatCurrency(effectiveRedemption) > formatCurrency(totalPool) ? `${formatCurrency(totalPool)} (Non-Match: ${formatCurrency(effectiveRedemption - totalPool)} )` : formatCurrency(totalPool)} / {formatCurrency(totalPool)}
 */}              Matched:{formatCurrency(totalMatch)}
              {totalNonMatch > 0 && ` Non-Match:${formatCurrency(totalNonMatch)}`}
              {totalRetracted > 0 && ` Redeemed:${formatCurrency(totalRetracted)}`}
            </div>
          </div>
        </Col>

        <Col xs={24} md={6}>
          {firmSegments.length > 0 ? (
            firmSegments.map((seg) => (
              <LegendItem
                key={seg.firm}
                color={seg.color}
                label={seg.firm}
                value={seg.amount}
                percent={seg.percent}
              />
            ))
          ) : (
            <LegendItem
              color={COLORS.progress}
              label="Total Redemption"
              value={effectiveRedemption}
              percent={progressPercent}
            />
          )}
        </Col>
      </Row>
    </Card>
  );
};

export default RedemptionPoolChart;
